import { Fragment, useMemo } from "react"
import { MapContainer, TileLayer, Marker, Popup, Tooltip, Polyline } from "react-leaflet"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import type { ProjectGridConnection } from "@/lib/api"

interface SubstationMapProps {
  projectName: string
  projectLatitude: number
  projectLongitude: number
  connections: ProjectGridConnection[]
}

const STATUS_COLORS: Record<string, string> = {
  GREEN: "#22c55e",
  AMBER: "#f59e0b",
  RED: "#ef4444",
}
const DEFAULT_STATUS_COLOR = "#94a3b8"

function statusColor(status: string | null): string {
  if (!status) return DEFAULT_STATUS_COLOR
  return STATUS_COLORS[status.toUpperCase()] ?? DEFAULT_STATUS_COLOR
}

function pinIcon(color: string, size = 26): L.DivIcon {
  return L.divIcon({
    className: "",
    html: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C7.6 0 4 3.6 4 8c0 5.4 7 15.4 7.3 15.8.2.3.6.5 1 .5s.8-.2 1-.5C12.7 23.4 20 13.4 20 8c0-4.4-3.6-8-8-8z"/>
      <circle cx="12" cy="8" r="3" fill="white" stroke="none"/>
    </svg>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  })
}

// Distinct "pulse dot" marker (vs. the substations' teardrop pins) so the
// project site is unmistakable at a glance.
function projectIcon(color: string, size = 30): L.DivIcon {
  return L.divIcon({
    className: "",
    html: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="11" fill="${color}" fill-opacity="0.22" stroke="none"/>
      <circle cx="12" cy="12" r="6.5" fill="${color}" stroke="white" stroke-width="2"/>
    </svg>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  })
}

const PROJECT_ICON = projectIcon("#6366f1", 30)

export function SubstationMap({
  projectName,
  projectLatitude,
  projectLongitude,
  connections,
}: SubstationMapProps) {
  const points = useMemo(() => {
    const substationPoints = connections
      .filter((c) => c.substation.latitude != null && c.substation.longitude != null)
      .map((c) => ({
        lat: Number(c.substation.latitude),
        lng: Number(c.substation.longitude),
      }))
    return [{ lat: projectLatitude, lng: projectLongitude }, ...substationPoints]
  }, [connections, projectLatitude, projectLongitude])

  const bounds = useMemo<L.LatLngBoundsExpression>(
    () => points.map((p) => [p.lat, p.lng] as [number, number]),
    [points],
  )

  return (
    <div className="h-[420px] w-full overflow-hidden rounded-lg border">
      <MapContainer
        bounds={bounds}
        boundsOptions={{ padding: [32, 32] }}
        className="h-full w-full"
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={[projectLatitude, projectLongitude]} icon={PROJECT_ICON}>
          <Tooltip direction="top" offset={[0, -18]}>
            {projectName}
          </Tooltip>
          <Popup>
            <div className="text-sm font-medium">{projectName}</div>
            <div className="text-xs text-muted-foreground">Project site</div>
          </Popup>
        </Marker>
        {connections.map((connection) => {
          const { substation } = connection
          if (substation.latitude == null || substation.longitude == null) return null
          const lat = Number(substation.latitude)
          const lng = Number(substation.longitude)
          const color = statusColor(substation.constraintStatus)
          return (
            <Fragment key={connection.id}>
              <Polyline
                positions={[
                  [projectLatitude, projectLongitude],
                  [lat, lng],
                ]}
                pathOptions={{ color: "#000000", weight: 2, opacity: 0.6, dashArray: "6 6" }}
              />
              <Marker position={[lat, lng]} icon={pinIcon(color)}>
                <Tooltip direction="top" offset={[0, -22]}>
                  #{connection.rank} {substation.name} · {Number(connection.distanceKm).toFixed(1)} km
                </Tooltip>
                <Popup>
                  <div className="space-y-1 text-sm">
                    <div className="font-medium">
                      #{connection.rank} {substation.name}
                    </div>
                    <div>Distance: {Number(connection.distanceKm).toFixed(2)} km</div>
                    <div>Status: {substation.constraintStatus ?? "Unknown"}</div>
                    {substation.headroomMw != null && (
                      <div>Headroom: {Number(substation.headroomMw).toFixed(2)} MW</div>
                    )}
                    {substation.dno && <div>DNO: {substation.dno}</div>}
                  </div>
                </Popup>
              </Marker>
            </Fragment>
          )
        })}
      </MapContainer>
    </div>
  )
}
